import {showNotification} from 'platform/components';
import {Box} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {PriceWithAndWithoutVat} from '@dms/api/shared';
import {useDeleteServiceOrderReturnItemMutation} from '@dms/api/warehouse';
import {i18n} from '@dms/i18n';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  handleApiError,
  useTenant,
} from '@dms/shared';
import {OverviewTotalPrice, useWarehouseParams} from '@dms/warehouse/shared-components';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

interface ListOfItemsTabProps extends RequiredTestIdProps {
  totalPrice: PriceWithAndWithoutVat | Nullish;
}

const PREDEFINED_COLUMNS = [] as const;

export function ListOfItemsTab(props: ListOfItemsTabProps) {
  const {serviceOrderReturnId} = useWarehouseParams();
  const tenant = useTenant();

  const formatCurrency = useFormatCurrency();
  const [deleteServiceOrderReturnItem] = useDeleteServiceOrderReturnItemMutation();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
  }) => {
    const id = isArray(rowId) ? rowId : [rowId];

    match(actionKey)
      .with('delete', () => {
        deleteServiceOrderReturnItem({serviceOrderReturnId, body: {serviceOrderReturnItemId: id}})
          .unwrap()
          .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.deleted')))
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {serviceOrderReturnId}]),
    [serviceOrderReturnId]
  );

  const totalPriceWithoutVat = formatCurrency(
    props.totalPrice?.withoutVat.amount ?? 0,
    props.totalPrice?.withoutVat?.currency ?? tenant.tenantCurrency,
    2
  );

  const totalPriceWithVat = formatCurrency(
    props.totalPrice?.withVat?.amount ?? 0,
    props.totalPrice?.withVat?.currency ?? tenant.tenantCurrency,
    2
  );

  return (
    <>
      <Box height="95%">
        <DmsDataGrid
          gridCode={WAREHOUSE_GRID_CODES.warehouseServiceOrderPartsReturnItem}
          queryModifier={queryModifier}
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={WAREHOUSE_GRID_CODES.warehouseServiceOrderPartsReturnItem}
        />
      </Box>
      <OverviewTotalPrice
        totalPriceWithoutVat={totalPriceWithoutVat}
        totalPriceWithVat={totalPriceWithVat}
        data-testid={suffixTestId('totalPrice', props)}
      />
    </>
  );
}
